import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type UserRole = "user" | "supervisor" | "manager" | "admin" | "owner";
export type PlatformAccess = "none" | "saas_marketing" | "saas_staff" | "super_admin";

interface UserWithRole {
  role: UserRole;
  isSuperAdmin?: boolean;
  platformAccess?: PlatformAccess | string | null;
  companyId?: number | null;
}

export function getPlatformAccess(user: UserWithRole | null | undefined): PlatformAccess {
  if (!user) return "none";
  const allowed: PlatformAccess[] = ["none", "saas_marketing", "saas_staff", "super_admin"];
  if (
    user.platformAccess &&
    user.platformAccess !== "none" &&
    allowed.includes(user.platformAccess as PlatformAccess)
  ) {
    return user.platformAccess as PlatformAccess;
  }
  return user.isSuperAdmin ? "super_admin" : "none";
}

export function hasSaasReadAccess(user: UserWithRole | null | undefined): boolean {
  const a = getPlatformAccess(user);
  return a === "saas_marketing" || a === "saas_staff" || a === "super_admin";
}

export function hasSaasWriteAccess(user: UserWithRole | null | undefined): boolean {
  const a = getPlatformAccess(user);
  return a === "saas_staff" || a === "super_admin";
}

export function isPlatformSuperAdmin(user: UserWithRole | null | undefined): boolean {
  return getPlatformAccess(user) === "super_admin";
}

// True when the user has SaaS-side access but no active company membership.
export function isSaasOnlyUser(user: UserWithRole | null | undefined): boolean {
  return hasSaasReadAccess(user) && !user?.companyId;
}

export function hasAdminAccess(user: UserWithRole | null | undefined): boolean {
  if (!user) return false;
  return user.role === "admin" || user.role === "owner" || user.isSuperAdmin === true;
}

export function hasManagerAccess(user: UserWithRole | null | undefined): boolean {
  if (!user) return false;
  return user.role === "manager" || hasAdminAccess(user);
}

export function hasSupervisorAccess(user: UserWithRole | null | undefined): boolean {
  if (!user) return false;
  return user.role === "supervisor" || hasManagerAccess(user);
}
